// Relatório ESG consolidado do PERÍODO (PO1-2401) — PDF client-side (jsPDF).
// O Excel agregado é gerado no backend (padrão Everlog); ver endpoint relatorio-xlsx.
// Reusa o molde/helpers do relatorioPdf.js (PO1-1996) e o `data` já carregado pelo
// dashboard (GET /indicadores/emissoes-carbono). Agregado do período, NÃO por-CTe —
// convive com o "Exportar CSV" por-CTe existente.
import { jsPDF } from "jspdf";
import {
  COR_PRIMARIA,
  COR_TEXTO,
  COR_LABEL,
  escreverLinha,
  fmtNumero,
} from "./relatorioPdf";
import {
  TITULO_ESG,
  SUBTITULO_ESG,
  FONTE_EQUIVALENCIAS,
  EQUIVALENCIA_LABELS,
  calcularEquivalencias,
  notaCobertura,
} from "./equivalencias";

const kgParaTon = (kg) => (Number(kg) || 0) / 1000;

// ---------- PDF agregado ----------

function checarQuebra(doc, y, pageH, necessario) {
  if (y + necessario > pageH - 16) {
    doc.addPage();
    return 20;
  }
  return y;
}

function desenharSecaoTabela(doc, startY, titulo, colunas, linhas, pageW, pageH) {
  let y = checarQuebra(doc, startY, pageH, 26);
  doc.setTextColor(...COR_PRIMARIA);
  doc.setFont(undefined, "bold");
  doc.setFontSize(11);
  doc.text(titulo, 15, y);
  doc.setFont(undefined, "normal");
  y += 6;

  doc.setFontSize(8);
  doc.setTextColor(...COR_LABEL);
  colunas.forEach((c) =>
    doc.text(c.label.toUpperCase(), c.x, y, c.align ? { align: c.align } : undefined)
  );
  y += 2;
  doc.setDrawColor(220, 220, 230);
  doc.setLineWidth(0.2);
  doc.line(15, y, pageW - 15, y);
  y += 5;

  doc.setFontSize(9);
  doc.setTextColor(...COR_TEXTO);
  if (!linhas.length) {
    doc.setTextColor(...COR_LABEL);
    doc.text("Sem dados no período.", 15, y);
    return y + 8;
  }
  linhas.forEach((linha) => {
    y = checarQuebra(doc, y, pageH, 8);
    colunas.forEach((c, i) => {
      const v = linha[i] != null ? String(linha[i]) : "-";
      doc.text(v, c.x, y, c.align ? { align: c.align } : undefined);
    });
    y += 6;
  });
  return y + 6;
}

export function gerarRelatorioEsgPdf(data, contexto = {}) {
  if (!data || !data.kpis) {
    throw new Error("Dados do período ausentes para gerar o relatório ESG.");
  }
  const { periodoLabel = "-", filtrosLabel = "", desde = "", ate = "" } = contexto;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  // Cabeçalho (banda primária) — mesmo padrão do relatório por-CTe.
  doc.setFillColor(...COR_PRIMARIA);
  doc.rect(0, 0, pageW, 28, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.text(`Relatório ESG do período: ${TITULO_ESG}`, 15, 14);
  doc.setFontSize(11);
  doc.text(SUBTITULO_ESG, 15, 22);

  let y = 40;
  doc.setTextColor(...COR_LABEL);
  doc.setFontSize(10);
  doc.text(`Período: ${periodoLabel}`, 15, y);
  if (filtrosLabel) {
    y += 6;
    doc.text(`Filtros: ${filtrosLabel}`, 15, y);
  }
  y += 12;

  // KPIs do período.
  const k = data.kpis;
  escreverLinha(doc, "Emissão total (t CO2)", fmtNumero(k.totalToneladasCO2, 3), 15, y);
  escreverLinha(doc, "Emissão total (kg CO2)", fmtNumero(k.totalKgCO2), 80, y);
  escreverLinha(doc, "CT-es com pegada calculada", fmtNumero(k.qtdCtes, 0), 145, y);
  y += 16;
  escreverLinha(doc, "Distância total (km)", fmtNumero(k.distanciaTotalKm), 15, y);
  escreverLinha(doc, "Emissão média (kg CO2/CT-e)", fmtNumero(k.emissaoMediaKgCO2), 80, y);
  y += 18;

  // Nota de cobertura (anti-greenwashing).
  const nota = notaCobertura(data.cobertura);
  if (nota) {
    doc.setTextColor(...COR_TEXTO);
    doc.setFontSize(9);
    doc.text(doc.splitTextToSize(nota, pageW - 30), 15, y);
    y += 10;
  }

  // Equivalências didáticas.
  const eq = calcularEquivalencias(k.totalToneladasCO2);
  doc.setTextColor(...COR_PRIMARIA);
  doc.setFont(undefined, "bold");
  doc.setFontSize(11);
  doc.text("Equivalências", 15, y);
  doc.setFont(undefined, "normal");
  y += 7;
  doc.setTextColor(...COR_TEXTO);
  doc.setFontSize(10);
  [
    `~ ${fmtNumero(eq.arvores, 0)} ${EQUIVALENCIA_LABELS.arvores}`,
    `~ ${fmtNumero(eq.carros, 0)} ${EQUIVALENCIA_LABELS.carros}`,
    `~ ${fmtNumero(eq.voos, 0)} ${EQUIVALENCIA_LABELS.voos}`,
    `~ ${fmtNumero(eq.gasolina, 0)} ${EQUIVALENCIA_LABELS.gasolina}`,
  ].forEach((linha) => {
    doc.text(linha, 18, y);
    y += 6;
  });
  y += 6;

  // Tabelas agregadas.
  const colsModal = [
    { label: "Modal", x: 15 },
    { label: "CT-es", x: 150, align: "right" },
    { label: "Emissão (t CO2)", x: 195, align: "right" },
  ];
  y = desenharSecaoTabela(
    doc,
    y,
    "Por modal",
    colsModal,
    (data.porModal || []).map((r) => [
      r.nomeModal || "-",
      fmtNumero(r.qtdCtes, 0),
      fmtNumero(kgParaTon(r.totalKgCO2), 3),
    ]),
    pageW,
    pageH
  );

  const colsTransp = [
    { label: "Transportadora", x: 15 },
    { label: "CT-es", x: 150, align: "right" },
    { label: "Emissão (t CO2)", x: 195, align: "right" },
  ];
  y = desenharSecaoTabela(
    doc,
    y,
    "Por transportadora (top 10)",
    colsTransp,
    (data.topTransportadoras || []).map((r) => [
      (r.nomeTransportadora || "-").slice(0, 48),
      fmtNumero(r.qtdCtes, 0),
      fmtNumero(kgParaTon(r.totalKgCO2), 3),
    ]),
    pageW,
    pageH
  );

  const colsMes = [
    { label: "Mês", x: 15 },
    { label: "CT-es", x: 150, align: "right" },
    { label: "Emissão (t CO2)", x: 195, align: "right" },
  ];
  y = desenharSecaoTabela(
    doc,
    y,
    "Evolução mensal",
    colsMes,
    (data.porMes || []).map((r) => [
      r.mes || "-",
      fmtNumero(r.qtdCtes, 0),
      fmtNumero(kgParaTon(r.totalKgCO2), 3),
    ]),
    pageW,
    pageH
  );

  // Rodapé de fontes + data de geração.
  y = checarQuebra(doc, y, pageH, 20);
  doc.setDrawColor(220, 220, 230);
  doc.line(15, y, pageW - 15, y);
  y += 6;
  doc.setTextColor(...COR_LABEL);
  doc.setFontSize(8);
  doc.text(doc.splitTextToSize(FONTE_EQUIVALENCIAS, pageW - 30), 15, y);
  const geradoEm = new Date().toLocaleString("pt-BR");
  doc.text(`Documento gerado em ${geradoEm} · Everlog Brasil`, 15, pageH - 10);

  doc.save(`relatorio-esg-${desde || "periodo"}-a-${ate || ""}.pdf`);
}
